import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosClient from "../../../../utils/api/axiosClient";
import { getToken } from "../../../../utils/functions/index.js";
import { showToast } from "../../../../utils/toast";

interface BackupFile {
  name: string;
  relativePath: string;
  sizeBytes: number;
  lastModified: number;
}

type DateRangeFilter = "all" | "today" | "7d" | "30d";
const AUTO_BACKUP_HOUR = 2;
const AUTO_BACKUP_MINUTE = 0;
const AUTO_BACKUP_SECOND = 0;

const formatSize = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** index;
  return `${value.toFixed(index === 0 ? 0 : 2)} ${units[index]}`;
};

const formatDate = (time: number) => {
  if (!time) return "-";
  return new Date(time).toLocaleString();
};

const hasTodayBackup = (files: BackupFile[]) => {
  const today = new Date();
  return files.some((file) => new Date(file.lastModified).toDateString() === today.toDateString());
};

const getNextAutoBackupTarget = (now = new Date()) => {
  const target = new Date(now);
  target.setHours(AUTO_BACKUP_HOUR, AUTO_BACKUP_MINUTE, AUTO_BACKUP_SECOND, 0);

  if (target.getTime() <= now.getTime()) {
    target.setDate(target.getDate() + 1);
  }

  return target;
};

const formatCountdown = (seconds: number) => {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  return [hours, minutes, remainingSeconds]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
};

function DbBackups() {
  const [files, setFiles] = useState<BackupFile[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [busyPath, setBusyPath] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [dateRange, setDateRange] = useState<DateRangeFilter>("all");
  const [runConfirmOpen, setRunConfirmOpen] = useState<boolean>(false);
  const [nextBackupCountdown, setNextBackupCountdown] = useState<number>(0);
  const [lastLoadSucceeded, setLastLoadSucceeded] = useState<boolean>(false);

  const autoBackupRequestedRef = useRef<boolean>(false);
  const backupRequestInFlightRef = useRef<boolean>(false);

  const headers = useMemo(() => ({ Authorization: getToken() }), []);

  const fetchBackups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get("/v1/admin/backups", { headers });
      if (res.status === 200) {
        setFiles(Array.isArray(res.data) ? res.data : []);
        setLastLoadSucceeded(true);
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "Failed to load backup files";
      showToast(message, "error");
      setFiles([]);
      setLastLoadSucceeded(false);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const target = getNextAutoBackupTarget(now);
      const remainingSeconds = Math.max(0, Math.floor((target.getTime() - now.getTime()) / 1000));
      setNextBackupCountdown(remainingSeconds);
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!lastLoadSucceeded || loading) return;
    if (backupRequestInFlightRef.current || autoBackupRequestedRef.current) return;
    if (hasTodayBackup(files)) return;

    autoBackupRequestedRef.current = true;
    backupRequestInFlightRef.current = true;
    showToast("No backup found for today. Creating one now.", "info");

    (async () => {
      try {
        const res = await axiosClient.post("/v1/admin/backups/run", {}, { headers });
        if (res.status === 200) {
          showToast(res.data?.message || "Backup created successfully", "success");
          await fetchBackups();
        }
      } catch (error: any) {
        const message = error?.response?.data?.message || "Failed to run backup";
        showToast(message, "error");
      } finally {
        backupRequestInFlightRef.current = false;
      }
    })();
  }, [files, headers, lastLoadSucceeded, loading, fetchBackups]);

  const handleDownload = async (file: BackupFile) => {
    setBusyPath(file.relativePath);
    try {
      const res = await axiosClient.get("/v1/admin/backups/download", {
        headers,
        params: { path: file.relativePath },
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: res.headers["content-type"] || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast("Backup download started", "success");
    } catch (error: any) {
      const message = error?.response?.data?.message || "Failed to download backup";
      showToast(message, "error");
    } finally {
      setBusyPath("");
    }
  };

  const handleDelete = async (file: BackupFile) => {
    const approved = window.confirm(`Delete backup file ${file.name}?`);
    if (!approved) return;

    setBusyPath(file.relativePath);
    try {
      const res = await axiosClient.delete("/v1/admin/backups", {
        headers,
        params: { path: file.relativePath },
      });

      if (res.status === 200) {
        setFiles((prev) => prev.filter((x) => x.relativePath !== file.relativePath));
        showToast("Backup deleted", "success");
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "Failed to delete backup";
      showToast(message, "error");
    } finally {
      setBusyPath("");
    }
  };

  const handleRunBackupNow = async () => {
    setBusyPath("__run__");
    backupRequestInFlightRef.current = true;
    try {
      const res = await axiosClient.post("/v1/admin/backups/run", {}, { headers });
      if (res.status === 200) {
        showToast(res.data?.message || "Backup created successfully", "success");
        await fetchBackups();
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "Failed to run backup";
      showToast(message, "error");
    } finally {
      backupRequestInFlightRef.current = false;
      setBusyPath("");
    }
  };

  const handleConfirmRunBackup = () => {
    setRunConfirmOpen(false);
    handleRunBackupNow();
  };

  const handleRefreshNow = () => {
    fetchBackups();
  };

  const isInDateRange = (lastModified: number, range: DateRangeFilter) => {
    if (range === "all") return true;

    const now = new Date();
    const fileDate = new Date(lastModified);

    if (range === "today") {
      return fileDate.toDateString() === now.toDateString();
    }

    const dayCount = range === "7d" ? 7 : 30;
    const threshold = now.getTime() - dayCount * 24 * 60 * 60 * 1000;
    return lastModified >= threshold;
  };

  const filteredFiles = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return files.filter((file) => {
      const matchesText =
        !search ||
        file.name.toLowerCase().includes(search) ||
        file.relativePath.toLowerCase().includes(search);
      const matchesDate = isInDateRange(file.lastModified, dateRange);
      return matchesText && matchesDate;
    });
  }, [files, searchText, dateRange]);

  const totalPages = useMemo(() => {
    const pages = Math.ceil(filteredFiles.length / pageSize);
    return pages > 0 ? pages : 1;
  }, [filteredFiles.length, pageSize]);

  const paginatedFiles = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredFiles.slice(start, start + pageSize);
  }, [filteredFiles, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, dateRange, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="app-content pt-3 p-md-3 p-lg-4">
      <div className="container-xl">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1 className="app-page-title mb-0">DB Backups</h1>
          <div className="d-flex gap-2">
            <button
              className="btn btn-success"
              onClick={() => setRunConfirmOpen(true)}
              disabled={busyPath === "__run__"}
            >
              {busyPath === "__run__" ? "Running..." : "Create Backup Now"}
            </button>
            <button className="btn app-btn-primary" onClick={handleRefreshNow} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        <div className="mb-3 d-flex flex-wrap gap-3 align-items-center">
          <small className="text-muted">Next auto backup in {formatCountdown(nextBackupCountdown)}</small>
          <small className="text-muted">Today backup check runs automatically on page load.</small>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
              <h5 className="mb-0">Available Backup Files</h5>
              <div className="d-flex gap-2 align-items-center">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search file/path"
                  style={{ minWidth: 220 }}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <select
                  className="form-select form-select-sm"
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value as DateRangeFilter)}
                >
                  <option value="all">All Dates</option>
                  <option value="today">Today</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
                <select
                  className="form-select form-select-sm"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={10}>10 / page</option>
                  <option value={25}>25 / page</option>
                  <option value={50}>50 / page</option>
                </select>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ minWidth: 220 }}>File Name</th>
                    <th style={{ minWidth: 260 }}>Path</th>
                    <th>Size</th>
                    <th>Last Modified</th>
                    <th style={{ minWidth: 200 }}>Options</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedFiles.length > 0 ? (
                    paginatedFiles.map((file) => {
                      const isBusy = busyPath === file.relativePath;
                      return (
                        <tr key={file.relativePath}>
                          <td>{file.name}</td>
                          <td>{file.relativePath}</td>
                          <td>{formatSize(file.sizeBytes)}</td>
                          <td>{formatDate(file.lastModified)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleDownload(file)}
                              disabled={isBusy}
                            >
                              Download
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(file)}
                              disabled={isBusy}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        {loading ? "Loading backups..." : "No backup files found."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between mt-3 gap-2">
              <small className="text-muted">
                Showing {filteredFiles.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                -{Math.min(currentPage * pageSize, filteredFiles.length)} of {filteredFiles.length}
              </small>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span className="align-self-center small text-muted">
                  Page {currentPage} / {totalPages}
                </span>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {runConfirmOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.45)", zIndex: 1050 }}
        >
          <div className="card shadow" style={{ width: "100%", maxWidth: 460 }}>
            <div className="card-header">
              <h5 className="mb-0">Create Backup Now</h5>
            </div>
            <div className="card-body">
              <p className="mb-0">Run DB backup now? This may take a few minutes.</p>
            </div>
            <div className="card-footer d-flex justify-content-end gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setRunConfirmOpen(false)}
              >
                Cancel
              </button>
              <button className="btn btn-success btn-sm" onClick={handleConfirmRunBackup}>
                Yes, Run Backup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DbBackups;
